/**
 * @fileoverview Org portal: a program's or a class's "Other resources".
 *
 * Files for the people teaching there: a safety plan and the school's floor
 * plan on the program, a classroom layout on a class. The program's
 * instructors see the program's; a class's instructors see the class's. They
 * are reference, not revisions: a new floor plan is uploaded and the old one
 * deleted.
 *
 * Owns its list. The section is absent while the kill switch is off (the
 * endpoints 404), rather than an empty card that cannot be filled.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Card, SectionHeader } from "@/components/studio/primitives";
import ResourceTiles from "@/components/ResourceTiles";
import {
  MAX_RESOURCES,
  MAX_RESOURCE_TITLE,
  RESOURCE_ACCEPT,
  RESOURCE_HINT,
  defaultTitle,
  uploadResource,
  validateResource,
} from "@/lib/resources";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

/**
 * @param {{ basePath: string, scope: "program"|"class", examples: string }} props
 *   basePath: `/org/programs/{id}` or `/org/classes/{id}`
 *   examples: what belongs here, for the empty state ("a safety plan, the school's floor plan")
 */
export default function ResourcesSection({ basePath, scope, examples }) {
  const [resources, setResources] = useState(null);
  const [available, setAvailable] = useState(true);
  // null | { mode: "add" } | { mode: "rename", resource }
  const [dialog, setDialog] = useState(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [progress, setProgress] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await orgApi.get(`${basePath}/resources`);
      setResources(res.data?.resources || []);
    } catch (err) {
      if (err?.response?.status === 404) setAvailable(false);
      else setResources([]);
    }
  }, [basePath]);

  useEffect(() => {
    load();
  }, [load]);

  if (!available) return null;

  const full = (resources || []).length >= MAX_RESOURCES;
  const who = scope === "class" ? "this class's instructors" : "everyone teaching in this program";

  const openAdd = () => {
    setFile(null);
    setTitle("");
    setProgress(null);
    setDialog({ mode: "add" });
  };

  const openRename = (resource) => {
    setTitle(resource.title);
    setDialog({ mode: "rename", resource });
  };

  const pick = (event) => {
    const picked = event.target.files?.[0];
    if (!picked) return;
    const problem = validateResource(picked);
    if (problem) {
      toast.error(problem);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setFile(picked);
    // The name the tile will show; the admin can change it before uploading.
    if (!title.trim()) setTitle(defaultTitle(picked.name));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (dialog?.mode === "rename") {
      setBusyId(dialog.resource.resource_id);
      try {
        await orgApi.patch(`${basePath}/resources/${dialog.resource.resource_id}`, { title: title.trim() });
        setDialog(null);
        await load();
      } catch (err) {
        toast.error(getOrgErrorMessage(err, "Couldn't rename that file"));
      } finally {
        setBusyId(null);
      }
      return;
    }
    if (!file) return;
    setProgress(0);
    try {
      await uploadResource(orgApi, basePath, file, { title, onProgress: setProgress });
      toast.success("Resource added");
      setDialog(null);
      await load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't upload that file"));
    } finally {
      setProgress(null);
    }
  };

  const remove = async (resource) => {
    if (!window.confirm(`Delete ${resource.title}? Instructors will no longer see it.`)) return;
    setBusyId(resource.resource_id);
    try {
      await orgApi.delete(`${basePath}/resources/${resource.resource_id}`);
      toast.success("Resource deleted");
      await load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't delete that file"));
    } finally {
      setBusyId(null);
    }
  };

  const uploading = progress !== null;

  return (
    <div className="mb-6" data-testid={`${scope}-resources`}>
      <SectionHeader
        action={full ? undefined : "+ Add resource"}
        onAction={openAdd}
        actionTestId={`${scope}-resources-add`}
      >
        Other resources{resources?.length ? ` (${resources.length})` : ""}
      </SectionHeader>
      {resources === null ? null : resources.length === 0 ? (
        <Card>
          <p className="font-sans text-[15px] text-[var(--text-tertiary)]" data-testid={`${scope}-resources-empty`}>
            Files for {who}: {examples}. They see them as tiles on their own pages.
          </p>
          <p className="mt-1 font-sans text-[13px] text-[var(--text-tertiary)]">{RESOURCE_HINT}</p>
        </Card>
      ) : (
        <>
          <ResourceTiles
            resources={resources}
            onRename={openRename}
            onDelete={remove}
            busyId={busyId}
            testId={`${scope}-resource-tiles`}
          />
          {/* Refused, not pruned: dropping the oldest drops the one someone needs. */}
          {full && (
            <p className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid={`${scope}-resources-full`}>
              This holds {MAX_RESOURCES} files. Delete one you no longer need to add another.
            </p>
          )}
        </>
      )}

      <Dialog open={Boolean(dialog)} onOpenChange={(open) => !open && !uploading && setDialog(null)}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-semibold">
              {dialog?.mode === "rename" ? "Rename resource" : "Add a resource"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} data-testid={`${scope}-resource-form`}>
            {dialog?.mode === "add" && (
              <>
                <input
                  ref={inputRef}
                  type="file"
                  accept={RESOURCE_ACCEPT}
                  onChange={pick}
                  className="hidden"
                  aria-label="Choose a file"
                  data-testid={`${scope}-resource-input`}
                />
                <Button type="button" variant="ghost" icon="upload" onPress={() => inputRef.current?.click()} disabled={uploading}>
                  {file ? "Choose a different file" : "Choose a file"}
                </Button>
                <p className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid={`${scope}-resource-picked`}>
                  {file ? file.name : RESOURCE_HINT}
                </p>
              </>
            )}
            <label className="mt-4 block">
              <span className="mb-1.5 block font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
                Name on the tile
              </span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={MAX_RESOURCE_TITLE}
                placeholder={scope === "class" ? "e.g. Classroom layout" : "e.g. Fire safety plan"}
                className={inputClass}
                data-testid={`${scope}-resource-title`}
              />
            </label>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button
                type="submit"
                disabled={dialog?.mode === "rename" ? !title.trim() || Boolean(busyId) : !file || uploading}
                data-testid={`${scope}-resource-save`}
              >
                {dialog?.mode === "rename" ? "Save" : uploading ? `Uploading… ${progress}%` : "Upload"}
              </Button>
              <button
                type="button"
                onClick={() => setDialog(null)}
                disabled={uploading}
                className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
              >
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
